import React from 'react';
import { Grid } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { heightStyle, marginBottomSize } from 'utility/sizeStyle';
import ReactDatepicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './style.css';
import { CalendarIcon } from '@heroicons/react/outline';
import dayjs from 'dayjs';

interface DatePickerProps {
  disabled?: boolean;
  name: string;
  placeholder?: string;
  required?: boolean;
  label: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  alignItems?: 'center' | 'stretch' | 'start' | 'end' | 'baseline';
  spacing?: number;
  wrap?: boolean;
  readOnly?: boolean;
  showInlineError?: boolean;
  visible?: boolean;
  onChange?: (val: Date, event: React.SyntheticEvent) => void;
}

function DatetimePicker(props: DatePickerProps) {
  const {
    name, required, label, disabled, size, alignItems, spacing, onChange, visible, showInlineError, wrap, readOnly, placeholder,
    ...rest
  } = props;

  const {
    control, formState: { errors },
  } = useFormContext();

  const disabledStyle = disabled ? 'bg-gray-50 border-dashed cursor-not-allowed text-inherit' : 'hover:border-gray-500';
  const readOnlyStyle = readOnly ? 'text-black bg-gray-50 active:ring-transparent focus:ring-transparent active:border-gray-200 focus:border-gray-200' : '';
  const errorStyle = get(errors, name) ? 'focus:border-red-600 hover:border-red-600 border-red-600' : '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDateChange = (date: Date | null, field: any, event: React.SyntheticEvent) => {
    field.onChange(date);
    if (onChange) onChange(date, event);
  };

  const inputWidth = () => {
    if (!label) return 12;
    if (wrap) return 8;
    return 12;
  };

  if (!visible) return null;

  return (
    <Grid container spacing={spacing || wrap ? 2 : 1} alignItems={alignItems} mb={marginBottomSize[size]}>
      <Grid item xs={wrap ? 4 : 12}>
        <span className={`text-${size}`}>{label}</span>
        {required && !disabled && <span className="text-red-600">*</span>}
      </Grid>
      <Grid item xs={inputWidth()}>
        <Controller
          name={name}
          control={control}
          defaultValue={null}
          rules={{
            required: required ? `Field '${label}' is required` : false,
          }}
          render={({ field }) => {
            const selectedDate = field.value && dayjs(field.value).isValid() ? dayjs(field.value).toDate() : null;

            return (
              <ReactDatepicker
                name={name}
                onBlur={field.onBlur}
                selected={selectedDate}
                onChange={(date: Date, event: React.SyntheticEvent) => handleDateChange(date, field, event)}
                dateFormat="dd/MM/yyyy HH:mm:ss"
                timeFormat="HH:mm"
                timeIntervals={15}
                disabled={disabled}
                readOnly={readOnly}
                todayButton="Today"
                showIcon
                toggleCalendarOnIconClick
                calendarIconClassName="!p-0 !flex !items-center !h-full !pl-1"
                icon={<CalendarIcon fontSize={12} />}
                isClearable={!disabled && !readOnly}
                wrapperClassName="w-full"
                className={`
                  p-1 py-0 border text-${size} border-gray-200 w-full focus:border-gray-200 focus:ring-0
                  placeholder:text-gray-300 ${heightStyle[size]} ${readOnly ? readOnlyStyle : disabledStyle} ${errorStyle}
                `}
                placeholderText={!disabled && !readOnly ? placeholder : ''}
                showWeekNumbers
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                showTimeSelect
                portalId="root"
                {...rest}
              />
            );
          }}
        />
      </Grid>
      {showInlineError && get(errors, name) && (
        <div className={`flex w-full justify-end text-red-600 text-${size} mb-${marginBottomSize[size]} mt-0.5`}>{get(errors, name).message?.toString()}</div>
      )}
    </Grid>
  );
}

DatetimePicker.defaultProps = {
  disabled: false,
  placeholder: 'dd/MM/yyyy hh:mm:ss',
  required: false,
  size: 'xs',
  wrap: true,
  readOnly: false,
  alignItems: 'center',
  spacing: null,
  showInlineError: false,
  onChange: () => null,
  visible: true,
};

export default DatetimePicker;
